import { useMutation, useQueryClient } from '@tanstack/react-query'
import { subscriptionQuery, subscriptionsQuery } from '../../account/queries'
import type { Household } from '../../api/households'
import {
  type Subscription,
  type SubscriptionChange,
  updateSubscription,
} from '../../api/subscriptions'
import { currentAuth } from '../../state/session'

export interface SubscriptionUpdate {
  subscription: Subscription
  change: SubscriptionChange
}

/**
 * Ask for a change to one of the household's subscriptions and keep every
 * view of it in step. What the API returns is Recharge's word on the
 * subscription now, with its next charge, so it is written straight into the
 * cache — the subscription's own query and the household list the cards
 * choose from — and nothing refetches after a save. The address book is the
 * address editor's to refresh, once every move it asked for has landed.
 */
export function useUpdateSubscription(household: Household) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ subscription, change }: SubscriptionUpdate) =>
      updateSubscription(subscription.id, change, { auth: currentAuth() }),
    onSuccess: (updated) => {
      queryClient.setQueryData(subscriptionQuery(updated.id).queryKey, updated)
      queryClient.setQueryData(subscriptionsQuery(household).queryKey, (subscriptions) =>
        subscriptions?.map((subscription) =>
          subscription.id === updated.id ? updated : subscription,
        ),
      )
    },
  })
}
